import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ICliente,
  IQueryParam,
  IProveedor,
  TipoProveedor,
  ICoordenadas,
  CategoriaProveedor,
  IFilter,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { ProveedoresService } from '../../../../auxiliares/servicios/http/proveedores.service';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { LoginService } from '../../../login/login.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MapaFacilService } from '../../../../auxiliares/mapa-facil/mapa-facil.service';
import { Coordinate } from '../../../../auxiliares/mapa-facil/interfaces';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectModule } from '@ng-select/ng-select';
import { MapaFacilComponent } from '../../../../auxiliares/mapa-facil/mapa-facil.component';
import { ButtonComponent } from '../../../../auxiliares/button/button.component';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
// import { OpenLayersService } from '../../../../auxiliares/servicios/openLayers.service'; // REMOVED: Migrated to Mapbox
import { ClienteSelectorComponent } from '../../../../componentes/selectores/cliente-selector/cliente-selector.component';
import { ContentCardComponent } from '../../../../componentes/content-card/content-card.component';
import { CardHeaderComponent } from '../../../../componentes/card-header/card-header.component';
import { LoadingComponent } from '../../../../componentes/loading/loading.component';

@Component({
  selector: 'app-crear-editar-proveedor',
  templateUrl: './crear-editar-proveedor.component.html',
  styleUrl: './crear-editar-proveedor.component.scss',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    MatIconModule,
    NgSelectModule,
    MapaFacilComponent,
    ButtonComponent,
    CdkDrag,
    CdkDragHandle,
    ClienteSelectorComponent,
    ContentCardComponent,
    CardHeaderComponent,
    LoadingComponent,
  ],
})
export class CrearEditarProveedorComponent implements OnInit, OnDestroy {
  data = inject<{
    edit: IProveedor;
    categoria: CategoriaProveedor;
  }>(MAT_DIALOG_DATA);
  private lista2 = inject(Lista2Service);
  dialogRef = inject<MatDialogRef<CrearEditarProveedorComponent>>(MatDialogRef);
  helper = inject(HelperService);
  private service = inject(ProveedoresService);
  private cdk = inject(OverlayContainer);
  private mapaFacilService = inject(MapaFacilService);

  public dragBoundary: HTMLElement | null = null;
  public clientePropio = LoginService.getCliente();
  public form?: FormGroup;
  public punto?: Coordinate;
  public title?: string;
  public tipos: { label: string; value: TipoProveedor }[] = [
    { label: 'Mecánico', value: 'Mecanico' },
    { label: 'Combustible', value: 'Combustible' },
    { label: 'Otro', value: 'Otro' },
  ];
  public categoria: CategoriaProveedor;
  public proveedor?: IProveedor;
  public loading: boolean = true;

  public marker$?: Subscription;
  public tipoCliente = HelperService.getTipoCliente();

  //CREAR FORM
  private createForm() {
    const clienteDefault =
      this.tipoCliente === 'Final'
        ? this.clientePropio._id
        : this.data?.edit?.idCliente;

    this.form = new FormGroup({
      categoria: new FormControl(
        this.data?.edit?.categoria || this.categoria,
        Validators.required,
      ),
      tipos: new FormControl(this.data?.edit?.tipos, Validators.required),
      idCliente: new FormControl(clienteDefault, Validators.required),
      nombre: new FormControl(this.data?.edit?.nombre, Validators.required),
      ubicacion: new FormControl(this.data?.edit?.ubicacion),
    });
  }

  public async onSubmit() {
    this.loading = true;
    try {
      const data = this.form.value;
      let res = null;
      if (this.data?.edit?._id) {
        res = await this.service.update(this.data.edit._id, data);
        this.helper.notifSuccess('Actualizado correctamente');
      } else {
        res = await this.service.create(data);
        this.helper.notifSuccess('Creado correctamente');
      }
      this.dialogRef.close(res);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  //MAPA
  public onPointChange(punto: Coordinate) {
    if (!punto || punto.length < 2) return;

    const coordUbicacion: ICoordenadas = {
      lng: punto[0],
      lat: punto[1],
    };
    this.form?.patchValue({ ubicacion: coordUbicacion });
    this.punto = punto;
  }

  public iniciarMapa() {
    // Si hay un proveedor con ubicación, usar esas coordenadas
    let coor: [number, number] = [-58.0128784, -35.5836812];
    if (this.proveedor?.ubicacion?.lng && this.proveedor?.ubicacion?.lat) {
      coor = [this.proveedor.ubicacion.lng, this.proveedor.ubicacion.lat];
      this.punto = coor;
    } else if (this.punto) {
      coor = this.punto;
    }

    this.mapaFacilService.updateData({
      center: coor,
      zoom: 15,
      ctrlZoom: true,
    });

    // Desuscribirse de la suscripción anterior si existe
    this.marker$?.unsubscribe();

    // Crear nueva suscripción
    this.marker$ = this.mapaFacilService.marker.subscribe((marker) => {
      this.onPointChange(marker);
    });
  }

  private setupDragBoundary(): void {
    // Intentar obtener el elemento router
    const routerElement = document.getElementById('router');

    if (routerElement) {
      this.dragBoundary = routerElement;
    } else {
      // Fallback: usar el contenedor del overlay como boundary
      const overlayContainer = this.cdk.getContainerElement();
      if (overlayContainer) {
        this.dragBoundary = overlayContainer;
      } else {
        // Último fallback: usar el viewport
        this.dragBoundary = document.body;
      }
    }
  }

  //HOOKS

  async ngOnInit() {
    this.loading = true;
    this.categoria = this.data.categoria;

    // Asignar proveedor si estamos editando
    if (this.data?.edit) {
      this.proveedor = this.data.edit;
      this.title = `Editar proveedor ${this.data.categoria}`;
    } else {
      this.title = `Crear proveedor ${this.data.categoria}`;
    }

    this.createForm();

    // Si es usuario final, establecer automáticamente el cliente
    if (this.tipoCliente === 'Final') {
      this.form?.patchValue({
        idCliente: this.clientePropio?._id,
      });
    }

    this.setupDragBoundary();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.marker$?.unsubscribe();
  }
}
